<template>
  <v-popover
    class="le-bubble-target"
    ref="bubble"
    :popoverBaseClass="['le-bubble', bubbleBaseClass]"
    :popoverInnerClass="['le-bubble-inner', bubbleInnerClass]"
    :popoverClass="theme"
    :popperOptions="popperOptions"
    :offset="offset"
    :hideOnTargetClick="hideOnTargetClick"
    :placement="placement"
    :autoHide="autoHide"
    :disabled="!isEnabled"
    :trigger="trigger"
    :container="container"
    :html="html"
    :delay="delay"
    :open="open"
    @show="onShow"
    @hide="onHide"
  >
    <slot name="target"></slot>
    <template slot="popover">
      <div ref="popoverContainer" @keydown.tab="onTab" @keydown.esc.prevent="onEsc">
        <slot name="content"></slot>
        <span v-close-popover>
          <i v-if="defaultCloseButton" ref="closeButton" class="close-icon ico-10 ico-close" role="button" tabindex="0" @click="hide" @keydown.enter.space.prevent="hide" :aria-label="closeAriaLabel"></i>
          <slot name="close" :aria-label="closeAriaLabel"></slot>
        </span>
      </div>
    </template>
  </v-popover>
</template>

<script>
  import CONSTS from '../../../consts/consts';

  export default {
    name: 'le-bubble',
    props: {
      closeAriaLabel: {
        type: String,
        required: false,
        default: 'Close',
      },
      isEnabled: {
        type: Boolean,
        default: true,
      },
      msg: {
        type: String,
        required: false,
      },
      offset: {
        type: Number,
        default: 5,
      },
      autoHide: {
        type: Boolean,
        default: false,
      },
      placement: {
        type: String,
        default: CONSTS.LE_BUBBLE.PLACEMENTS.BOTTOM,
      },
      trigger: {
        type: String,
        required: false,
      },
      html: {
        type: Boolean,
        required: false,
      },
      container: {
        type: String,
        required: false,
      },
      delay: {
        type: Object,
        required: false,
      },
      open: {
        type: Boolean,
        required: false,
      },
      hideOnTargetClick: {
        type: Boolean,
      },
      bubbleBaseClass: {
        type: String,
        required: false,
      },
      theme: {
        type: String,
        required: false,
      },
      bubbleInnerClass: {
        type: String,
        required: false,
      },
      defaultCloseButton: {
        type: Boolean,
        required: false,
      },
      flip: {
        type: Boolean,
        default: true,
      },
      boundariesElement: {
        type: String,
        default: 'window',
      },
    },
    data() {
      return {
        popperOptions: {
          modifiers: {
            flip: {
              enabled: this.flip,
            },
            preventOverflow: {
              boundariesElement: this.boundariesElement,
            },
          },
        },
      };
    },
    methods: {
      onShow() {
        this.$emit('onShow');
      },
      onHide() {
        this.$emit('hide');
      },
      onTab(event) {
        const focusableNodeList = this.$refs.bubble.$refs.popover.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const focusableArr = Array.prototype.slice.call(focusableNodeList);
        const currentIndex = focusableArr.indexOf(event.target);
        // treat like modal and trap focus in the bubble
        if (this.defaultCloseButton) {
          if (currentIndex === 0 && event.shiftKey) {
            event.preventDefault();
            if (focusableArr.length > 0) {
              focusableArr[focusableArr.length - 1].focus();
            }
          }
          if (currentIndex === focusableArr.length - 1 && !event.shiftKey) {
            event.preventDefault();
            if (focusableArr.length > 0) {
              focusableArr[0].focus();
            }
          }
        }
        // close the bubble and send focus back to the trigger
        if (!this.defaultCloseButton && ((currentIndex === 0 && event.shiftKey) || (currentIndex === focusableArr.length - 1 && !event.shiftKey))) {
          event.preventDefault();
          this.hide();
        }
      },
      onEsc() {
        this.hide();
      },
      show() {
        this.$refs.bubble.show();
        this.$refs.bubble.$refs.popover.removeAttribute('tabindex');
        setTimeout(() => {
          if (this.defaultCloseButton) {
            this.$refs.closeButton.focus();
          } else {
            const focusableElement = this.$refs.bubble.$refs.popover.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusableElement) {
              focusableElement.focus();
            }
          }
        }, 250);
      },
      hide() {
        this.$refs.bubble.hide();
        this.$nextTick(() => {
          const focusableElement = this.$refs.bubble.$refs.trigger.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
          if (focusableElement) {
            focusableElement.focus();
          }
        });
      },
      toggle() {
        if (this.open) {
          this.hide();
        } else {
          this.show();
        }
        this.open = !this.open;
      },
    },
  };
</script>

<style lang="scss">
  .le-bubble-target {
    .trigger {
      width: 100%;
    }
  }

  .le-bubble {
    /* free parameters. play with these to change the bubble properties */
    $content-max-width: 568px;
    $content-pad: 15px;
    $content-border-width: 1px;
    $content-border-radius: 3px;
    $outer-arrow-altitude: 7px;
    $transition-duration: .15s;

    /* computed variables. no reason to change those */
    $inner-arrow-altitude: $outer-arrow-altitude - $content-border-width + round($content-border-width / 2);
    $inner-arrow-offset: $inner-arrow-altitude - ($content-border-width + ($content-border-width / 2));
    $close-icon-offset: $content-pad - 3; /* close-icon size is hard coded to be 10 pixels (see the template) */

    outline: none;
    z-index: 1999;

    .le-bubble-inner {
      height: 100%;
      max-width: $content-max-width;
      overflow: auto; /* serves as clearfix for when floating elements inside the bubble are higher than surrounding text */
      padding: $content-pad;
      border-radius: $content-border-radius;
      line-height: 1.4;
      font-family: $font-family-Roboto;
      border: $content-border-width solid;
      @include themify {
        color: getColor('dialogText');
        background-color: getColor('dialogBackground');
        border-color: getColor('dialogBorder');
      }
    }

    .close-icon {
      position: absolute;
      top: $close-icon-offset;
      right: $close-icon-offset;
      cursor: pointer;
      @include themify {
        color: getColor('dialogText');
      }
    }

    .tooltip-arrow {
      position: absolute;
      z-index: 1;
      display: block;
      width: 0;
      height: 0;
      border: $outer-arrow-altitude solid transparent;

      &:after {
        content: '';
        position: absolute;
        display: inline-block;
        border: $inner-arrow-altitude solid transparent;
      }
    }

    &[x-placement^="top"] {
      .wrapper {
        padding-bottom: $outer-arrow-altitude;
      }
      .tooltip-arrow {
        bottom: $outer-arrow-altitude * -1;
        &:after {
          bottom: $inner-arrow-offset * -1;
          left: $inner-arrow-altitude * -1;
        }
      }
    }
    @include themify {
      &[x-placement^="top"] {
        .le-bubble-inner {
          box-shadow: 0 2px 2px 1px getColor('dialogShadow');
        }
        .tooltip-arrow {
          border-top-color: getColor('dialogBorder');
          &:after {
            border-top-color: getColor('dialogBackground');
          }
        }
      }
    }

    &[x-placement^="bottom"] {
      .wrapper {
        padding-top: $outer-arrow-altitude;
      }
      .tooltip-arrow {
        top: $outer-arrow-altitude * -1;

        &:after {
          top: $inner-arrow-offset * -1;
          left: $inner-arrow-altitude * -1;
        }
      }
    }
    @include themify {
      &[x-placement^="bottom"] {
        .le-bubble-inner {
          box-shadow: 0 2px 2px 1px getColor('dialogShadow');
        }
        .tooltip-arrow {
          border-bottom-color: getColor('dialogBorder');
          &:after {
            border-bottom-color: getColor('dialogBackground');
          }
        }
      }
    }

    &[x-placement^="right"] {
      .wrapper {
        padding-left: $outer-arrow-altitude;
      }
      .tooltip-arrow {
        left: $outer-arrow-altitude * -1;

        &:after {
          top: $inner-arrow-altitude * -1;
          left: $inner-arrow-offset * -1;
        }
      }
    }
    @include themify {
      &[x-placement^="right"] {
        .le-bubble-inner {
          box-shadow: 0 2px 2px 1px getColor('dialogShadow');
        }
        .tooltip-arrow {
          border-right-color: getColor('dialogBorder');
          &:after {
            border-right-color: getColor('dialogBackground');
          }
        }
      }
    }

    &[x-placement^="left"] {
      .wrapper {
        padding-right: $outer-arrow-altitude;
      }
      .tooltip-arrow {
        right: $outer-arrow-altitude * -1;

        &:after {
          top: $inner-arrow-altitude * -1;
          right: $inner-arrow-offset * -1;
        }
      }
    }
    @include themify {
      &[x-placement^="left"] {
        .le-bubble-inner {
          box-shadow: 0 2px 2px 1px getColor('dialogShadow');
        }
        .tooltip-arrow {
          border-left-color: getColor('dialogBorder');
          &:after {
            border-left-color: getColor('dialogBackground');
          }
        }
      }
    }

    &[aria-hidden] {
      transition: opacity $transition-duration, visibility $transition-duration;
    }

    &[aria-hidden="true"] {
      visibility: hidden;
      opacity: 0;
    }

    &[aria-hidden="false"] {
      visibility: visible;
      opacity: 1;
    }

    /*
    &.dark {
      .le-bubble-inner {
        border-color: $color-gray-88;
        color: $color-gray-dd;
        background-color: $color-gray-32;
        box-shadow: 0 2px 3px rgba(50, 51, 53, 0.35);
      }

      .close-icon {
        color: $color-gray-dd;
      }

      &[x-placement^="top"] {
        .tooltip-arrow {
          border-top-color: $color-gray-88;

          &:after {
            border-top-color: $color-gray-32;
          }
        }
      }

      &[x-placement^="bottom"] {
        .tooltip-arrow {
          border-bottom-color: $color-gray-88;

          &:after {
            border-bottom-color: $color-gray-32;
          }
        }
      }

      &[x-placement^="right"] {
        .tooltip-arrow {
          border-right-color: $color-gray-88;

          &:after {
            border-right-color: $color-gray-32;
          }
        }
      }

      &[x-placement^="left"] {
        .tooltip-arrow {
          border-left-color: $color-gray-88;

          &:after {
            border-left-color: $color-gray-32;
          }
        }
      }
    }
    */
  }

</style>
